import React, { useEffect, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  deleteEmploymentHistory,
  fetchEmploymentHistoryList,
} from "../../../services/api/employment";
import { useCountries } from "../../../hooks/useCountries";
import dayjs from "dayjs";
import AddEmpolymentHistoryModal from "../../CompleteYourProfile/components/AddEmpolymentHistoryModal";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";

const AddEditEmployment = ({ userId }) => {
  const [isAddEmploymentModalOpen, setIsAddEmploymentModalOpen] = useState(false);
  const [employmentToDelete, setEmploymentToDelete] = useState(null);
  const [selectedEmployment, setSelectedEmployment] = useState();
  const isEdit = selectedEmployment ? true : false;
  const [employmentHistoryList, setEmploymentHistoryList] = useState([]);
  const { getCountryLabel } = useCountries();
  const {
    data: employmentListData,
    isPending: isFetchEmploymentListLoading,
    error: isFetchEmploymentListError,
    refetch: refetchEmploymentHistoryList,
  } = useQuery({
    queryKey: ["employmentHistoryList", userId],
    queryFn: () => fetchEmploymentHistoryList(userId),
    enabled: !!userId,
  });

  const {
    isPending: isDeletedEmploymentApiLoading,
    error: isDeletedEmploymentApiError,
    data: deletedEmploymentData,
    mutateAsync: deleteEmploymentHistoryMutation,
  } = useMutation({
    mutationFn: (id) => deleteEmploymentHistory(id, userId),
    enabled: false,
  });

  useEffect(() => {
    if (employmentListData) {
      setEmploymentHistoryList(employmentListData?.data);
    }
  }, [employmentListData]);

  const handleDeleteEmployment = async () => {
    if (!employmentToDelete) return;
    const { id } = employmentToDelete;
    try {
      await deleteEmploymentHistoryMutation(id);
      setEmploymentHistoryList((prevEmployment) => prevEmployment.filter((item) => item.id !== id));
      setEmploymentToDelete(null);
    } catch (error) {}
  };

  const handleEditButton = (employment) => {
    try {
      setSelectedEmployment(employment);
      setIsAddEmploymentModalOpen(true);
    } catch (error) {}
  };

  return (
    <div className="mt-6 border-b pb-4">
      <div className="bg-light px-4 py-2 rounded-md flex justify-between items-center ">
        <h6 className="text-dark font-normal">Employment History</h6>
        <i
          className="fa-solid fa-circle-plus cursor-pointer text-lg text-secondary"
          onClick={() => setIsAddEmploymentModalOpen(true)}
        ></i>
      </div>

      {employmentHistoryList?.map((employment) => (
        <div className="bg-light rounded-md py-3 px-4 flex justify-between border mt-4">
          <div>
            <div className="flex mb-1.5">
              <p className="font-medium mr-2">Company Name: </p>
              <p>{employment?.companyName}</p>
            </div>
            <div className="flex mb-1.5">
              <p className="font-medium mr-2">Designation: </p>
              <p>{employment?.designation}</p>
            </div>
            <div className="flex mb-1.5">
              <p className="font-medium mr-2">Country: </p>
              <p>{getCountryLabel(employment?.country)}</p>
            </div>
            <div className="flex">
              <div className="flex ">
                <p className="font-medium">Start Date: </p>
                <p className="ml-2">
                  {dayjs(employment?.startDate).format("DD/MM/YYYY")}
                  {!employment?.endDate && " (Present)"}
                </p>
              </div>
              {employment.endDate && (
                <div className="flex mb-1.5 ml-9">
                  <p className="font-medium">End Date: </p>
                  <p className="ml-2">{dayjs(employment?.endDate).format("DD/MM/YYYY")}</p>
                </div>
              )}
            </div>
            <div className="mb-1.5">
              <p className="font-medium mr-2">Experience: </p>
              <div
                className="ql-editor !min-h-0 !p-0 text-justify"
                dangerouslySetInnerHTML={{ __html: employment?.description || "" }}
              />
            </div>
          </div>
          <div className="flex">
            <i
              className="fa-regular fa-trash-can text-red-600 cursor-pointer mr-3"
              onClick={() => setEmploymentToDelete(employment)}
            ></i>
            <i
              className="fa-light fa-pencil text-gray-900 cursor-pointer"
              onClick={() => handleEditButton(employment)}
            ></i>
          </div>
        </div>
      ))}

      {employmentToDelete && (
        <BaseConfirmModal
          open={!!employmentToDelete}
          onClose={() => setEmploymentToDelete(null)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          type="warning"
          title="Delete Employment History"
          description={`Are you sure you want to delete your employment record at "${employmentToDelete?.companyName}"? This action cannot be undone.`}
          isLoading={isDeletedEmploymentApiLoading}
          onConfirm={handleDeleteEmployment}
        />
      )}

      {isAddEmploymentModalOpen && (
        <AddEmpolymentHistoryModal
          open={isAddEmploymentModalOpen}
          close={setIsAddEmploymentModalOpen}
          setEmploymentHistoryList={setEmploymentHistoryList}
          selectedEmployment={selectedEmployment}
          setSelectedEmployment={setSelectedEmployment}
          isEdit={isEdit}
          targetUserId={userId}
        />
      )}
    </div>
  );
};

export default AddEditEmployment;
